import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { colors, mono, cardShadow } from "../theme/tokens";

interface Task {
  id: string;
  text: string;
  done: boolean;
}

const formatToday = () =>
  new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

/**
 * The interactive task list card shown next to the hero copy.
 * Clicking a task toggles it, so visitors get a feel for the app before signing up.
 */
const TodoMockup: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([
    { id: "1", text: "Ship the v2 dashboard", done: false },
    { id: "2", text: "Write onboarding email", done: false },
  ]);

  const toggleTask = (id: string) =>
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, done: !t.done } : t))
    );

  const openCount = tasks.filter((t) => !t.done).length;

  return (
    <Box
      sx={{
        width: "100%",
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: "20px",
        p: 3.5,
        boxShadow: cardShadow,
      }}
    >
      <Typography
        sx={{
          fontFamily: mono,
          fontSize: "11px",
          letterSpacing: "0.06em",
          color: colors.faint,
          textTransform: "uppercase",
          mb: 1.25,
        }}
      >
        {formatToday()}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
        <Typography
          component="span"
          sx={{ fontSize: "26px", fontWeight: 800, color: colors.ink }}
        >
          Tasks
        </Typography>
        <Box
          component="span"
          sx={{
            background: colors.primaryTint,
            color: colors.primary,
            fontSize: "12px",
            fontWeight: 700,
            px: 1.25,
            py: 0.5,
            borderRadius: "999px",
          }}
        >
          {openCount} open
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#F6F5FE",
          border: "1px solid #DFDCF9",
          borderRadius: "12px",
          px: 2,
          py: 1.75,
          mb: 2.75,
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            component="span"
            sx={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              border: "1.5px dashed #A9A3EF",
            }}
          />
          <Typography sx={{ color: colors.faint, fontSize: "14px" }}>
            Add a task
          </Typography>
        </Box>
        <Typography
          sx={{ fontFamily: mono, fontSize: "11px", color: "#A9A3EF" }}
        >
          ⌘K anywhere
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.75 }}>
        <Box
          component="span"
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: colors.primary,
          }}
        />
        <Typography
          sx={{
            fontFamily: mono,
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.05em",
            color: colors.primary,
          }}
        >
          WEBSITE
        </Typography>
        <Typography sx={{ fontSize: "11px", color: colors.fainter }}>
          {tasks.length}
        </Typography>
        <Box sx={{ flex: 1, height: "1px", background: colors.line }} />
      </Box>

      {tasks.map((task) => (
        <Box
          key={task.id}
          onClick={() => toggleTask(task.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleTask(task.id);
            }
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            py: 1.25,
            px: "2px",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <Box
            component="span"
            sx={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              border: `1.5px solid ${task.done ? colors.primary : "#D7D5EF"}`,
              background: task.done ? colors.primary : "transparent",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: "10px",
              transition: "all 0.2s ease",
            }}
          >
            {task.done ? "✓" : ""}
          </Box>
          <Typography
            sx={{
              fontSize: "15px",
              color: task.done ? colors.fainter : colors.ink,
              textDecoration: task.done ? "line-through" : "none",
              transition: "color 0.2s ease",
            }}
          >
            {task.text}
          </Typography>
        </Box>
      ))}

      <Box sx={{ height: "1px", background: colors.line, my: 1.75 }} />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: "13px",
          color: colors.faint,
        }}
      >
        <Typography sx={{ fontSize: "13px", color: colors.faint }}>
          Completed
        </Typography>
        <Box
          component="span"
          sx={{
            background: "#F1F1F4",
            px: 1,
            py: "2px",
            borderRadius: "999px",
            fontSize: "11px",
          }}
        >
          {tasks.filter((t) => t.done).length + 1}
        </Box>
      </Box>
    </Box>
  );
};

export default TodoMockup;
